import { sortBy, sumBy } from './utils';
import {
  MaterialPatternString,
  parsePatternString,
  PatternString,
  splitPatternString,
  SubBoardPattern,
  toPatternString,
} from 'Pattern';
import { KeyboardEvent } from 'react';
import { Bumps, Material } from 'Types';

const A_CODE = 'A'.charCodeAt(0);

export function bumpSelection<T extends PatternString>(
  event: KeyboardEvent<HTMLInputElement>,
  bumpFn: (number: number, bump: number) => number,
  bumps: Bumps,
  setSelection: (selection: [number, number]) => void
): T {
  const { target } = event;

  if (!(target instanceof HTMLInputElement)) {
    throw new Error('cant bump');
  }

  const { startFragmentIndex, endFragmentIndex } =
    getPatternInputSelection(target);

  const pattern = parsePatternString(target.value as T);

  for (let i = startFragmentIndex; i <= endFragmentIndex; ++i) {
    const fragmentToBump = pattern[i];
    fragmentToBump.size = bumpFn(
      fragmentToBump.size,
      event.shiftKey ? bumps.shiftBump : bumps.bump
    );
  }

  const patternString = toPatternString(pattern);

  const patternStringFragments = splitPatternString(patternString);
  const leadingFragments = patternStringFragments.slice(0, startFragmentIndex);
  const selectedFragments = patternStringFragments.slice(
    startFragmentIndex,
    endFragmentIndex + 1
  );

  const leadingFragmentsLength =
    sumBy(leadingFragments, (f) => f.length) + leadingFragments.length;
  const selectedFragmentsLength =
    sumBy(selectedFragments, (f) => f.length) + selectedFragments.length;

  setSelection([
    leadingFragmentsLength,
    leadingFragmentsLength + selectedFragmentsLength - 1,
  ]);

  return patternString as T;
}

export function subpatternIndexToKey(index: number): string {
  return String.fromCharCode(A_CODE + index);
}

export function subpatternKeyToIndex(key: string): number {
  return key.charCodeAt(0) - A_CODE;
}

export function getSubBoardThicknessesByKey(pattern: SubBoardPattern): {
  [key: string]: { [size: number]: number };
} {
  const subBoardThicknessesByKey: {
    [key: string]: { [size: number]: number };
  } = {};

  sortBy(pattern, ['size', 'key']).forEach(({ key, size }) => {
    if (key == null) {
      return;
    }

    if (subBoardThicknessesByKey[key] == null) {
      subBoardThicknessesByKey[key] = {};
    }

    if (subBoardThicknessesByKey[key][size] == null) {
      subBoardThicknessesByKey[key][size] = 0;
    }

    subBoardThicknessesByKey[key][size] += size;
  });

  return subBoardThicknessesByKey;
}

export function getPatternInputSelection(input: HTMLInputElement) {
  const patternString = input.value;
  let selectionStart = input.selectionStart ?? 0;
  let selectionEnd = input.selectionEnd ?? 0;

  // Nudge the selection to include the entire fragment
  while (selectionStart > 0 && !/\s/.test(patternString[selectionStart - 1])) {
    selectionStart--;
  }
  while (
    selectionEnd < patternString.length &&
    !/\s/.test(patternString[selectionEnd])
  ) {
    selectionEnd++;
  }

  const startFragmentIndex = splitPatternString(
    patternString.slice(0, selectionStart) as PatternString
  ).length;
  const numSelectedFragments =
    splitPatternString(
      patternString.slice(selectionStart, selectionEnd) as PatternString
    ).length - 1;
  const endFragmentIndex = startFragmentIndex + numSelectedFragments;

  return {
    startFragmentIndex,
    endFragmentIndex,
  };
}

export function getMaxPatternFragmentSize(pattern: Array<{ size: number }>) {
  return Math.max(...pattern.map((fragment) => fragment.size));
}

export function getMaterialsInPattern(patternString: MaterialPatternString): Set<Material> {
  const result = new Set<Material>()

  for (const { key } of parsePatternString(patternString)) {
    if (key != null && key in Material) {
      result.add(key)
    }
  }

  return result
}

export function swapMaterialInPatternString(
  patternString: MaterialPatternString,
  targetMaterial: Material,
  newMaterial: Material
) {
  const pattern = parsePatternString(patternString)

  for (const fragment of pattern) {
    if (targetMaterial === fragment.key) {
      fragment.key = newMaterial
    }
  }

  return toPatternString(pattern)
}